import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DropResult } from "react-beautiful-dnd";

import { addReviewStage, reorderReviewStages } from "../../../redux/actions/reviewStagesActions";
import { IReviewStage } from "../../../types/review-stage/ireview-stage.interface";
import { selectReviewStagesLoader } from "../../../redux/selectors/reviewStages";
import usePermissions from "../../../hooks/usePermissions";

import AddIcon from "../../icon/icons/layer-22-icon.component";
import { LoaderDelay, SpinnerLoader } from "../../loaders";
import { OutlinedButton } from "../../form-controls/button/outlined-button/outlined-button.component";

import { AddReviewStage } from "../add-review-stage/add-review-stage.component";
import { ReviewStageDraggableListView } from "../review-stage-draggable-list-view/review-stage-draggable-list-view.component";

import { ReviewStageListProps } from "./review-stage-list.props";

export const ReviewStageList: React.FC<ReviewStageListProps> = (props) => {
  const { loaded = true, stages } = props;

  const dispatch = useDispatch();
  const isLoading = useSelector(selectReviewStagesLoader);
  const [newReviewerLoading, setNewReviewerLoading] = useState<boolean>(isLoading);
  const [showAddReviewer, setShowAddReviewer] = useState(false);
  const [newReviewStage, setNewReviewState] = useState({} as IReviewStage);
  const [draggableStageList, setDraggableStageList] = useState(stages);
  const userPermissions = usePermissions();

  const showAddReviewStage = useMemo(
    () => Boolean(userPermissions.manageReviewers && showAddReviewer),
    [userPermissions, showAddReviewer]
  );

  useEffect(() => {
    setDraggableStageList(stages);
    if (!isLoading) {
      setNewReviewerLoading(false);
    }
  }, [stages, isLoading]);

  const updateNewStage = (stage: IReviewStage) => {
    setNewReviewerLoading(true);
    setTimeout(() => {
      const newOrderNumber = stages.length > 0 ? stages[stages.length - 1].order + 1 : 1;
      const createdStage = {
        ...stage,
        order: newOrderNumber,
        parentId: Number(props.id),
      };
      dispatch(addReviewStage(props.pageType, createdStage));
      setNewReviewState(createdStage);
    }, LoaderDelay);
  };

  const handleAddReviewer = () => {
    setShowAddReviewer(true);
  };

  const closeAddReviewer = () => {
    setShowAddReviewer(false);
  };

  const handleDragEnd = ({ destination, draggableId }: DropResult) => {
    const movedStage = stages.filter((stage) => draggableId === `${stage.id}`);
    const remainingStages = stages.filter((stage) => draggableId !== `${stage.id}`);

    if (destination) {
      const updatedList = Array.from(remainingStages.slice(0, destination.index));
      updatedList.push(...movedStage);
      updatedList.push(...remainingStages.slice(destination.index));

      updatedList.forEach((reviewer, index) => {
        reviewer.order = index;
      });

      const reorderedIds = updatedList.map(({ id }) => id);
      dispatch(reorderReviewStages(Number(props.id), props.pageType, reorderedIds));
      setDraggableStageList(updatedList);
    }
  };

  return (
    <>
      <SpinnerLoader showIf={!loaded} size={40} />

      <ReviewStageDraggableListView
        showIf={loaded}
        canManageReviewers={userPermissions.manageReviewers}
        pageType={props.pageType}
        stages={draggableStageList}
        onDragEnd={handleDragEnd}
      >
        <AddReviewStage
          showIf={showAddReviewStage}
          reviewStage={newReviewStage}
          updateNewStage={updateNewStage}
          close={closeAddReviewer}
        />
        <SpinnerLoader showIf={newReviewerLoading} size={40} />
      </ReviewStageDraggableListView>

      <OutlinedButton
        showIf={userPermissions.manageReviewers && props.briefStatus !== "approved"}
        onClick={handleAddReviewer}
        variant="outlined"
        startIcon={<AddIcon />}
      >
        Add Approvers
      </OutlinedButton>
    </>
  );
};
